import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, RefreshCw } from "lucide-react";
import { api, asItems, formatApiError } from "@/lib/api";
import AgentCard from "@/components/agents/AgentCard";
import { Button } from "@/components/ui/button";

export default function VerifiedAgentsSection({ activeCity }) {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let live = true;
    const loadAgents = async () => {
      setLoading(true);
      setError(null);
      try {
        // Issue #5: the homepage must stay in sync with the full "All Agents"
        // listing and always surface multiple agents. We fetch the same source
        // (/agents) and simply prioritise agents in the active city, filling the
        // remaining slots from the rest so a city with a single local agent no
        // longer collapses the section down to one card.
        const res = await api.get("/agents");
        let items = asItems(res.data);
        if (activeCity) {
          const norm = String(activeCity).trim().toLowerCase();
          items = [...items].sort((a, b) => {
            const am = String(a.city || "").trim().toLowerCase() === norm ? 0 : 1;
            const bm = String(b.city || "").trim().toLowerCase() === norm ? 0 : 1;
            return am - bm;
          });
        }
        if (live) {
          setAgents(items.slice(0, 6));
        }
      } catch (err) {
        if (live) {
          setAgents([]);
          setError(formatApiError(err));
        }
      } finally {
        if (live) {
          setLoading(false);
        }
      }
    };

    loadAgents();
    return () => { live = false; };
  }, [activeCity, reloadKey]);

  return (
    <section className="py-16 bg-white/40 border-b border-slate-200/40" data-testid="verified-agents-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
        {/* Header Row */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
          <div className="border-l-4 border-emerald-500 pl-4">
            <h2 className="font-display text-2xl sm:text-3xl font-black tracking-tight text-slate-900">
              Verified Agents
            </h2>
            <p className="text-xs text-muted-foreground mt-1">
              Connect with trusted local property professionals in your preferred city.
            </p>
          </div>
          <Link 
            to="/agents" 
            className="inline-flex items-center gap-1.5 text-xs font-bold text-[#0078DB] hover:text-[#005cab] transition shrink-0"
            data-testid="explore-all-agents-link"
          >
            Explore All Agents <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-80 rounded-2xl shimmer border border-slate-200" />
            ))}
          </div>
        )}

        {/* Error State */}
        {!loading && error && (
          <div className="text-center py-12 rounded-2xl border border-warning/30 bg-warning/10" role="alert">
            <p className="text-sm font-bold text-slate-900">Verified agents could not be loaded.</p>
            <p className="mt-1 text-xs text-slate-600">{error}</p>
            <Button type="button" size="sm" variant="outline" className="mt-4 gap-2" onClick={() => setReloadKey((value) => value + 1)}><RefreshCw className="h-4 w-4" aria-hidden="true"/>Retry</Button>
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && agents.length === 0 && (
          <div className="text-center py-12 bg-white rounded-2xl border border-dashed border-slate-200 shadow-sm max-w-lg mx-auto p-8">
            <p className="text-slate-600 font-semibold mb-2">
              No verified agents are available in this city yet.
            </p>
            <p className="text-slate-400 text-xs mb-6">
              Connect with agents from other regions to assist with your property search.
            </p>
            <Button asChild className="bg-[#0078DB] hover:bg-[#005cab] text-white font-bold"><Link to="/agents">Explore All Agents</Link></Button>
          </div>
        )}

        {/* Results grid / Horizontal Carousel */}
        {!loading && !error && agents.length > 0 && (
          <div className="flex gap-6 overflow-x-auto scrollbar-none snap-x snap-mandatory flex-nowrap md:grid md:grid-cols-2 lg:grid-cols-3 lg:overflow-visible pb-4 lg:pb-0 -mx-6 px-6 md:mx-0 md:px-0">
            {agents.map((a) => (
              <div 
                key={a.user_id || a.id} 
                className="snap-center shrink-0 w-[85%] sm:w-[48%] lg:w-auto"
              >
                <AgentCard agent={a} />
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
